import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { fetchRetentionOffer, acceptRetentionOffer } from "@/lib/api";

const NAVY = "#0A1628";
const GOLD = "#D4AF37";
const BG = "#F8F9FA";

const fmt = (cents) => `$${((cents || 0) / 100).toFixed(0)}`;

const planLabel = (plan) => ({
  starter: "Bronze (Starter)",
  growth: "Gold (NearNow Autopilot)",
  pro: "Platinum (NearNow Autopilot Super)",
  elite: "AutoPilot Growth",
  market_leader: "Market Leader",
}[plan] || (plan || "").toUpperCase());

export default function RetentionSavePage() {
  const { token } = useParams();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchRetentionOffer(token);
        if (!cancelled) {
          setOffer(data);
          if (data.status === "accepted") setAccepted({ ok: true, ...data });
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.status === 404 ? "not_found" : "error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const accept = async () => {
    setAccepting(true);
    try {
      const result = await acceptRetentionOffer(token);
      setAccepted(result);
    } catch (e) {
      setError(e?.response?.data?.detail || "accept_failed");
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="retention-loading" style={pageWrap}>
        <p style={{ color: "#5b5e69" }}>Loading your offer…</p>
      </div>
    );
  }

  if (error === "not_found" || !offer) {
    return (
      <div data-testid="retention-not-found" style={pageWrap}>
        <h1 style={h1Style}>OFFER NOT FOUND</h1>
        <p style={{ color: "#5b5e69", fontSize: 18 }}>
          That offer link doesn&apos;t exist or has been revoked. If you think this is a mistake,
          email <a href="mailto:contact@nearnow.biz" style={{ color: NAVY }}>contact@nearnow.biz</a>.
        </p>
      </div>
    );
  }

  if (offer.status === "expired") {
    return (
      <div data-testid="retention-expired" style={pageWrap}>
        <h1 style={h1Style}>OFFER EXPIRED</h1>
        <p style={{ color: "#5b5e69", fontSize: 18 }}>
          This Anniversary Save offer has expired. Email us at{" "}
          <a href="mailto:contact@nearnow.biz" style={{ color: NAVY }}>contact@nearnow.biz</a>{" "}
          if you&apos;d still like to talk about your subscription.
        </p>
      </div>
    );
  }

  if (offer.status === "declined" || offer.status === "reverted") {
    return (
      <div data-testid="retention-closed" style={pageWrap}>
        <h1 style={h1Style}>OFFER CLOSED</h1>
        <p style={{ color: "#5b5e69", fontSize: 18 }}>
          This offer is no longer active. Reach out to us at{" "}
          <a href="mailto:contact@nearnow.biz" style={{ color: NAVY }}>contact@nearnow.biz</a>.
        </p>
      </div>
    );
  }

  if (accepted?.ok || offer.status === "accepted") {
    return (
      <div data-testid="retention-accepted" style={pageWrap}>
        <div style={successBox}>
          <div style={{ color: GOLD, letterSpacing: 3, fontSize: 14, fontWeight: 700, marginBottom: 8 }}>
            ANNIVERSARY SAVE · CONFIRMED
          </div>
          <h1 style={h1Style}>YOU&apos;RE SAVED.</h1>
          <p style={{ fontSize: 20, color: NAVY, marginTop: 4, marginBottom: 24 }}>
            Your subscription is now <strong>$49/month for 90 days</strong>.
          </p>
          <div style={breakdownGrid}>
            <div style={breakdownTile}>
              <div style={tileLabel}>NEW PRICE</div>
              <div style={tileValue}>$49<span style={{ fontSize: 18, color: "#5b5e69" }}>/mo</span></div>
            </div>
            <div style={breakdownTile}>
              <div style={tileLabel}>DURATION</div>
              <div style={tileValue}>90 <span style={{ fontSize: 18, color: "#5b5e69" }}>days</span></div>
            </div>
            <div style={breakdownTile}>
              <div style={tileLabel}>WAS</div>
              <div style={tileValue}>{planLabel(offer.current_plan)}</div>
            </div>
          </div>
          <p style={{ color: "#5b5e69", fontSize: 15, marginTop: 28 }}>
            After 90 days, your subscription will return to <strong>{planLabel(offer.current_plan)}</strong>{" "}
            automatically. ARIA keeps answering your calls. Your leads keep coming.
          </p>
          <a href="https://nearnow.biz/leads"
             data-testid="retention-go-dashboard"
             style={ctaPrimary}>
            OPEN MY DASHBOARD →
          </a>
        </div>
      </div>
    );
  }

  // status === "offered"
  const ownerLabel = offer.owner_name || offer.business_name || "there";

  return (
    <div data-testid="retention-offer" style={pageWrap}>
      <div style={card}>
        <div style={{ color: GOLD, letterSpacing: 3, fontSize: 14, fontWeight: 700, marginBottom: 8 }}>
          ANNIVERSARY SAVE · ONE-TIME OFFER
        </div>
        <h1 style={h1Style}>WAIT — DON&apos;T GO YET.</h1>
        <p style={{ fontSize: 20, color: NAVY, lineHeight: 1.5, marginTop: 4 }}>
          Hi <strong>{ownerLabel}</strong>. Before you cancel, here&apos;s a deal we save for
          our best contractors:
        </p>

        <div style={offerBoxStyle}>
          <div style={offerLine}>
            <span style={offerStrike}>{fmt(offer.current_plan === "pro" ? 14900 : 9700)}/mo</span>
            <span style={offerArrow}>→</span>
            <span style={offerNew}>{fmt(offer.save_price_cents)}/mo</span>
          </div>
          <div style={offerDuration}>for the next 90 days</div>
        </div>

        <ul style={bulletList}>
          <li>Keep <strong>ARIA voice answering</strong> on every missed call</li>
          <li>Keep your leads dashboard and contractor profile</li>
          <li>Keep your reviews, your reputation, your local visibility</li>
          <li>After 90 days, you return to <strong>{planLabel(offer.current_plan)}</strong> automatically. No tricks.</li>
        </ul>

        <button
          data-testid="retention-accept-button"
          onClick={accept}
          disabled={accepting}
          style={{ ...ctaPrimary, opacity: accepting ? 0.65 : 1, cursor: accepting ? "wait" : "pointer", border: "none", width: "100%" }}>
          {accepting ? "Saving you…" : `ACCEPT $49 SAVE →`}
        </button>

        {error && error !== "not_found" && (
          <p data-testid="retention-error" style={{ color: "#A32D2D", marginTop: 14, fontSize: 14 }}>
            Something went wrong: {String(error)}. Email contact@nearnow.biz and we&apos;ll apply it manually.
          </p>
        )}

        <p style={{ color: "#5b5e69", fontSize: 13, marginTop: 18 }}>
          One per contractor lifetime. Offer expires {offer.expires_at ? new Date(offer.expires_at).toLocaleDateString() : "soon"}.
          No setup, no calls, no fine print. One click and you&apos;re back.
        </p>
      </div>
    </div>
  );
}

// ---------- styles ----------
const pageWrap = { background: BG, minHeight: "calc(100vh - 200px)", padding: "60px 20px", display: "flex", justifyContent: "center" };
const card = { maxWidth: 640, width: "100%", background: "#fff", borderRadius: 18, padding: "44px 38px", border: `2px solid ${GOLD}`, boxShadow: "0 30px 60px -25px rgba(10,22,40,0.25)" };
const successBox = { ...card, background: "#fff" };
const h1Style = { fontFamily: "Bebas Neue, Arial, sans-serif", letterSpacing: 2.5, fontSize: 52, lineHeight: 1.05, color: NAVY, margin: "0 0 16px" };
const offerBoxStyle = { background: NAVY, color: "#fff", borderRadius: 14, padding: "28px 22px", textAlign: "center", margin: "28px 0 22px" };
const offerLine = { display: "flex", alignItems: "center", justifyContent: "center", gap: 16, flexWrap: "wrap" };
const offerStrike = { textDecoration: "line-through", color: "#9ca3af", fontSize: 22 };
const offerArrow = { color: GOLD, fontSize: 26, fontWeight: 700 };
const offerNew = { color: GOLD, fontSize: 42, fontWeight: 700, fontFamily: "Bebas Neue, Arial, sans-serif", letterSpacing: 1.5 };
const offerDuration = { color: "#cbd5e1", marginTop: 6, fontSize: 16, letterSpacing: 1 };
const bulletList = { lineHeight: 1.85, paddingLeft: 22, margin: "0 0 28px", color: NAVY, fontSize: 17 };
const ctaPrimary = { display: "inline-block", textAlign: "center", background: GOLD, color: NAVY, textDecoration: "none", padding: "20px 28px", borderRadius: 12, fontWeight: 700, letterSpacing: 2, fontSize: 19, fontFamily: "Bebas Neue, Arial, sans-serif", marginTop: 10 };
const breakdownGrid = { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginTop: 8 };
const breakdownTile = { background: "#F8F9FA", border: "1px solid #e5e7ee", borderRadius: 12, padding: "16px 12px", textAlign: "center" };
const tileLabel = { color: "#5b5e69", letterSpacing: 2, fontSize: 11, fontWeight: 700 };
const tileValue = { color: NAVY, fontFamily: "Bebas Neue, Arial, sans-serif", fontSize: 38, letterSpacing: 1, marginTop: 4 };
